// src/features/inventory/utils/inventoryExcel.js

import * as XLSX from "xlsx";
import { safeNumber, normalizeNumber, parseBoolean } from "./inventoryHelpers";
import { EXCEL_HEADER_MAP } from "./inventoryConstants";

/**
 * پارس کردن فایل اکسل به داده‌های JSON خام با استفاده از FileReader
 */
export const parseExcelFile = (file) => {
  return new Promise((resolve, reject) => {
    if (!(file instanceof Blob)) {
      return reject(new Error("فایل ارسال شده نامعتبر است."));
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];

        // تبدیل شیت به آرایه‌ای از آبجکت‌ها با مقدار پیش‌فرض خالی برای سلول‌های خالی
        const rows = XLSX.utils.sheet_to_json(worksheet, { defval: "" });
        resolve(rows);
      } catch (err) {
        reject(new Error("پارس کردن ساختار فایل اکسل با خطا مواجه شد."));
      }
    };
    reader.onerror = () => reject(new Error("خطا در خواندن فایل از روی سیستم."));
    reader.readAsArrayBuffer(file);
  });
};

/**
 * تبدیل فیلدهای ردیف‌های خام اکسل بر اساس هدرهای فارسی به فرمت فرانت‌اند
 */
export const mapExcelToInventory = (rawRows = []) => {
  if (!Array.isArray(rawRows)) return [];

  return rawRows.map((row, idx) => {
    // پیدا کردن مقادیر با هدرهای معادل یا جایگزین انگلیسی
    const getValue = (persianKey) => {
      const engKey = EXCEL_HEADER_MAP[persianKey];
      return row[persianKey] !== undefined ? row[persianKey] : row[engKey];
    };

    const id = safeNumber(getValue("شناسه"));

    return {
      rowIndex: idx + 1,
      id: id,
      product_code: String(getValue("کد کالا") || "").trim(),
      name: String(getValue("نام کالا") || "").trim(),
      brand: String(getValue("برند") || "").trim(),
      category_name: String(getValue("دسته‌بندی") || "").trim(),
      price: normalizeNumber(getValue("قیمت"), "0"),
      stock: normalizeNumber(getValue("موجودی"), "0"),
      discount_percent: normalizeNumber(getValue("درصد تخفیف"), "0"),
      min_order: normalizeNumber(getValue("حداقل سفارش"), "1"),
      max_order: normalizeNumber(getValue("حداکثر سفارش"), ""),
      order_multiple: normalizeNumber(getValue("مضرب سفارش"), "1"),
      inventory_is_active: parseBoolean(getValue("وضعیت موجودی")),
      isDirty: true,
    };
  });
};

/**
 * آماده‌سازی Payload نهایی برای ارسال به API ثبت گروهی
 */
export const prepareBulkUpdatePayload = (previewRows = [], warehouseId) => {
  if (!warehouseId) {
    throw new Error("شناسه انبار برای ثبت گروهی الزامی است.");
  }

  // فیلتر کردن ردیف‌هایی که فاقد شناسه کالا هستند
  const validItems = previewRows
    .filter(row => row.id !== null && row.id !== undefined)
    .map(row => ({
      id: Number(row.id),
      price: safeNumber(row.price, 0),
      stock: safeNumber(row.stock, 0),
      discount_percent: safeNumber(row.discount_percent, 0),
      min_order: safeNumber(row.min_order, 1),
      max_order: safeNumber(row.max_order, null), // مقدار null برای فاقد سقف سفارش
      order_multiple: safeNumber(row.order_multiple, 1),
      is_active: Boolean(row.inventory_is_active)
    }));

  return {
    warehouse_id: Number(warehouseId),
    items: validItems
  };
};

export const buildExcelHeaders = () => ({
  id: "شناسه",
  name: "نام کالا",
  product_code: "کد کالا",
  brand: "برند",
  category: "دسته‌بندی",
  price: "قیمت",
  stock: "موجودی",
  discount_percent: "درصد تخفیف",
  min_order: "حداقل سفارش",
  max_order: "حداکثر سفارش",
  order_multiple: "مضرب سفارش",
  is_active: "وضعیت کالا",
  inventory_is_active: "وضعیت موجودی",
});
